import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Check, Plus, Pencil } from "lucide-react";
import { useAppData } from "../data/AppDataContext";
import { useAuth } from "../auth/AuthContext";
import { api } from "../api/client";
import { naira, formatDate } from "../data/format";
import StatusPill from "../components/StatusPill";
import AccountTypeTag from "../components/AccountTypeTag";
import { EQUIPMENT_OPTIONS, sanitizeEquipmentName, canonicalEquipment, isUnspecifiedName } from "../data/equipment";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customerDetails, fetchCustomerDetail, salesPeople } = useAppData();
  const { user } = useAuth();
  const canEdit = ["admin", "team_lead", "manager"].includes(user?.role);

  const [copiedToken, setCopiedToken] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [editingName, setEditingName] = useState(false);
  const [nameForm, setNameForm] = useState({ name: "", phone: "", email: "" });
  const [savingName, setSavingName] = useState(false);

  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editForm, setEditForm] = useState({ equipment_name: "", sales_person: "" });
  const [editingPaymentDates, setEditingPaymentDates] = useState({});
  const [savingPlan, setSavingPlan] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    fetchCustomerDetail(id).catch((e) => setLoadError(e.message));
  }, [id, fetchCustomerDetail]);

  const customer = customerDetails[id];

  if (loadError) {
    return (
      <div>
        <div className="pl-empty">{loadError}</div>
        <Link to="/customers" className="pl-btn-link">Back to customers</Link>
      </div>
    );
  }

  if (!customer) {
    return <div className="pl-empty">Loading...</div>;
  }

  const plans = customer.plans || [];
  // A plan with no equipment name reads "Plan 01", "Plan 02" ... by its place among this customer's plans (same as the server)
  const planOrder = [...plans].sort((a, b) => a.id - b.id).map((p) => p.id);
  const planLabel = (plan) =>
    isUnspecifiedName(plan.equipment_name)
      ? `Plan ${String(planOrder.indexOf(plan.id) + 1).padStart(2, "0")}`
      : plan.equipment_name;
  const summary = plans.reduce(
    (acc, plan) => {
      acc.total += Number(plan.total_price);
      acc.paid += Number(plan.total_paid);
      acc.balance += Number(plan.balance);
      return acc;
    },
    { total: 0, paid: 0, balance: 0 }
  );

  function copyLink(link) {
    if (navigator.clipboard) navigator.clipboard.writeText(link).catch(() => {});
    setCopiedToken(link);
    setTimeout(() => setCopiedToken(null), 1600);
  }

  function startEditName() {
    setNameForm({ name: customer.name, phone: customer.phone || "", email: customer.email || "" });
    setEditingName(true);
  }

  async function saveName() {
    setSavingName(true);
    try {
      await api.updateCustomer(customer.id, nameForm);
      await fetchCustomerDetail(customer.id);
      setEditingName(false);
    } finally {
      setSavingName(false);
    }
  }

  function startEditPlan(plan) {
    setEditingPlanId(plan.id);
    // an unnamed plan starts with an empty box instead of the placeholder text
    setEditForm({ equipment_name: isUnspecifiedName(plan.equipment_name) ? "" : plan.equipment_name, sales_person: plan.sales_person || "" });
    setEditError("");
    const dates = {};
    (plan.payments || []).forEach((p) => { dates[p.id] = p.submitted_at?.slice(0, 10) || ""; });
    setEditingPaymentDates(dates);
  }

  function setEquipmentName(value) {
    setEditForm((f) => ({ ...f, equipment_name: sanitizeEquipmentName(value) }));
    setEditError("");
  }

  function commitEquipmentName() {
    setEditForm((f) => ({ ...f, equipment_name: canonicalEquipment(sanitizeEquipmentName(f.equipment_name, { final: true })) }));
  }

  function cancelPlanEdit() {
    setEditingPlanId(null);
    setEditError("");
  }

  async function savePlanEdit(plan) {
    const equipment = canonicalEquipment(sanitizeEquipmentName(editForm.equipment_name, { final: true }));
    const wasUnnamed = isUnspecifiedName(plan.equipment_name);
    // an unnamed plan may stay unnamed; a named one must keep a real name
    if (!equipment && !wasUnnamed) { setEditError("Enter or pick the equipment."); return; }
    if (equipment && equipment.length < 2) { setEditError("Equipment name is too short."); return; }
    setSavingPlan(true);
    try {
      const body = { sales_person: editForm.sales_person || null };
      if (equipment) body.equipment_name = equipment;
      await api.updatePlan(plan.id, body);
      for (const payment of plan.payments || []) {
        const newDate = editingPaymentDates[payment.id];
        if (newDate && newDate !== payment.submitted_at?.slice(0, 10)) {
          await api.editPayment(payment.id, { submitted_at: newDate });
        }
      }
      await fetchCustomerDetail(customer.id);
      setEditingPlanId(null);
    } finally {
      setSavingPlan(false);
    }
  }

  return (
    <div>
      <button
        className="pl-btn-link"
        style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14 }}
        onClick={() => navigate("/customers")}
      >
        <ArrowLeft size={14} /> Back to customers
      </button>

      {editingName ? (
        <div className="pl-panel" style={{ maxWidth: 460, padding: 20, marginBottom: 18 }}>
          <div className="pl-field">
            <label>Name</label>
            <input value={nameForm.name} onChange={(e) => setNameForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="pl-field">
            <label>Phone</label>
            <input value={nameForm.phone} onChange={(e) => setNameForm((f) => ({ ...f, phone: e.target.value }))} />
          </div>
          <div className="pl-field">
            <label>Email</label>
            <input type="email" value={nameForm.email} onChange={(e) => setNameForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="pl-btn pl-btn-confirm" disabled={savingName} onClick={saveName}>
              {savingName ? "Saving..." : "Save"}
            </button>
            <button className="pl-btn pl-btn-ghost" onClick={() => setEditingName(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
          <div className="pl-page-title" style={{ marginBottom: 0 }}>{customer.name}</div>
          <AccountTypeTag type={customer.account_type} />
          {canEdit && (
            <button className="pl-btn pl-btn-ghost" onClick={startEditName}>
              <Pencil size={12} /> Edit
            </button>
          )}
        </div>
      )}
      {!editingName && (
        <div className="pl-page-sub">{[customer.phone, customer.email].filter(Boolean).join(" - ") || "-"}</div>
      )}

      <div className="pl-link-row">
        <span className="pl-token">{customer.link}</span>
        <button className="pl-btn pl-btn-ghost" onClick={() => copyLink(customer.link)}>
          {copiedToken === customer.link ? <Check size={12} /> : <Copy size={12} />}
          {copiedToken === customer.link ? "Copied" : "Copy link"}
        </button>
        <span className="pl-meta">One link - shows every plan below.</span>
      </div>

      <div className="pl-stat-grid pl-stat-grid-3">
        <div className="pl-stat-card">
          <div className="pl-stat-label">Plans</div>
          <div className="pl-stat-value">{plans.length}</div>
        </div>
        <div className="pl-stat-card">
          <div className="pl-stat-label">Total paid</div>
          <div className="pl-stat-value blue">{naira(summary.paid)}</div>
        </div>
        <div className="pl-stat-card">
          <div className="pl-stat-label">Balance owed</div>
          <div className="pl-stat-value red">{naira(summary.balance)}</div>
        </div>
      </div>

      <div className="pl-page-header-row">
        <div className="pl-section-title">Plans</div>
        {canEdit && (
          <Link to={`/new-plan?customerId=${customer.id}`} className="pl-btn pl-btn-confirm" style={{ marginBottom: 14 }}>
            <Plus size={14} /> New plan for {customer.name.split(" ")[0]}
          </Link>
        )}
      </div>

      {plans.length === 0 && <div className="pl-empty">No plans yet.</div>}

      {plans.map((plan) => {
        const history = [...(plan.payments || [])].sort((a, b) => (a.submitted_at < b.submitted_at ? 1 : -1));
        const isEditingThis = editingPlanId === plan.id;
        return (
          <div className="pl-card" key={plan.id}>
            <div className="pl-plan-card-header">
              <div>
                <div style={{ fontWeight: 700 }}>{planLabel(plan)}</div>
                <div className="pl-meta">
                  {plan.duration_months ? `${plan.duration_months} months` : ""}
                  {plan.sales_person_name ? ` - sold by ${plan.sales_person_name}` : ""}
                  {plan.created_by_role_display ? ` - Created by ${plan.created_by_role_display}` : ""}
                </div>
              </div>
              <div className="pl-plan-card-figures">
                <div className="pl-figure-block">
                  <div className="pl-stat-label">Total</div>
                  <div style={{ fontWeight: 700 }}>{naira(plan.total_price)}</div>
                </div>
                <div className="pl-figure-block">
                  <div className="pl-stat-label">Paid</div>
                  <div style={{ fontWeight: 700, color: "var(--blue-700)" }}>{naira(plan.total_paid)}</div>
                </div>
                <div className="pl-figure-block">
                  <div className="pl-stat-label">Balance</div>
                  <div style={{ fontWeight: 700, color: "var(--red-700)" }}>{naira(plan.balance)}</div>
                </div>
                {canEdit && !isEditingThis && (
                  <button className="pl-btn pl-btn-ghost" onClick={() => startEditPlan(plan)}>
                    <Pencil size={12} /> Edit
                  </button>
                )}
              </div>
            </div>

            {isEditingThis && (
              <div style={{ background: "var(--blue-50)", border: "1px solid var(--blue-100)", borderRadius: 10, padding: 14, marginTop: 14 }}>
                <div className="pl-field">
                  <label>Equipment</label>
                  <input
                    value={editForm.equipment_name}
                    onChange={(e) => setEquipmentName(e.target.value)}
                    onBlur={commitEquipmentName}
                    placeholder="Type the equipment name..."
                    autoComplete="off"
                  />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                    {EQUIPMENT_OPTIONS.map((eq) => {
                      const active = editForm.equipment_name.trim().toLowerCase() === eq.toLowerCase();
                      return (
                        <button
                          key={eq}
                          type="button"
                          className={`pl-btn ${active ? "pl-btn-confirm" : "pl-btn-ghost"}`}
                          style={{ padding: "4px 10px", fontSize: 12 }}
                          onClick={() => { setEditForm((f) => ({ ...f, equipment_name: eq })); setEditError(""); }}
                        >
                          {eq}
                        </button>
                      );
                    })}
                  </div>
                  {editError && <div className="pl-field-error">{editError}</div>}
                </div>
                <div className="pl-field">
                  <label>Sales person</label>
                  <select value={editForm.sales_person} onChange={(e) => setEditForm((f) => ({ ...f, sales_person: e.target.value }))}>
                    <option value="">Not set</option>
                    {salesPeople.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                {history.length > 0 && (
                  <>
                    <div className="pl-section-title" style={{ marginTop: 12 }}>Payment dates</div>
                    {history.map((p) => (
                      <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, width: 110 }}>{naira(p.amount)}</span>
                        <input
                          type="date"
                          value={editingPaymentDates[p.id] || ""}
                          onChange={(e) => setEditingPaymentDates((d) => ({ ...d, [p.id]: e.target.value }))}
                        />
                      </div>
                    ))}
                  </>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button className="pl-btn pl-btn-confirm" disabled={savingPlan} onClick={() => savePlanEdit(plan)}>
                    {savingPlan ? "Saving..." : "Save changes"}
                  </button>
                  <button className="pl-btn pl-btn-ghost" onClick={cancelPlanEdit}>Cancel</button>
                </div>
              </div>
            )}

            {!isEditingThis && history.length > 0 && (
              <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 10 }}>
                {history.map((p) => (
                  <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, gap: 10, flexWrap: "wrap" }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>{naira(p.amount)}</span>{" "}
                      <span className="pl-meta">
                        - {formatDate(p.submitted_at)}
                        {p.note ? ` - ${p.note}` : ""}
                      </span>
                    </div>
                    <StatusPill status={p.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}